/**
 * Row header renderer with add/remove controls for Handsontable v16
 */
import type Handsontable from 'handsontable-v16';
import addBtn from '@/assets/images/add-circle-line.svg?raw';
import removeBtn from '@/assets/images/indeterminate-circle-line.svg?raw';
import styles from './HoT.module.scss';

/**
 * Row header renderer that adds add/remove row controls
 *
 * Used with afterGetRowHeader hook to render custom row headers
 * with interactive buttons for adding and removing rows.
 *
 * @param this - Handsontable Core instance (bound by HOT)
 * @param row - Row index
 * @param TH - Table header cell element
 */
export const rowHeaderWithControlsRendererV16 = function (
  this: Handsontable.Core,
  row: number,
  TH: HTMLTableHeaderCellElement
): void {
  TH.innerHTML = '';
  TH.setAttribute('data-test-element', 'row-header-with-controls');
  TH.setAttribute('data-test-key', `row-${row}`);

  const container = document.createElement('div');
  container.classList.add(styles.Container);

  if (this.countRows() === row + 1) {
    // Last row - show add button
    const svgContainer = document.createElement('span');
    svgContainer.setAttribute('data-test-element', 'add-row-button');
    svgContainer.classList.add(styles.Add);
    svgContainer.innerHTML = addBtn;

    svgContainer.onmousedown = (event: MouseEvent) => {
      if (!(this.getActiveEditor()?.isOpened() ?? false)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    svgContainer.onclick = () => {
      if (!(this.getActiveEditor()?.isOpened() ?? false)) {
        this.alter('insert_row_above', row, 1, 'add-row.click');
        this.setDataAtCell(row, 0, this.getDataAtCell(row, 0) ?? null, 'add-row.click');
      }
    };

    container.appendChild(svgContainer);
  } else {
    // Other rows - show row number and remove button
    const rowNumber = document.createElement('span');
    rowNumber.classList.add(styles.Number);
    rowNumber.innerText = `${row + 1}`;

    const svgContainer = document.createElement('span');
    svgContainer.setAttribute('data-test-element', 'remove-row-button');
    svgContainer.classList.add(styles.Remove);
    svgContainer.innerHTML = removeBtn;

    svgContainer.onmousedown = (event: MouseEvent) => {
      if (!(this.getActiveEditor()?.isOpened() ?? false)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    svgContainer.onclick = () => {
      if (!(this.getActiveEditor()?.isOpened() ?? false)) {
        this.alter('remove_row', row, 1, 'remove-row.click');
      }
    };

    container.appendChild(rowNumber);
    container.appendChild(svgContainer);
  }

  TH.appendChild(container);
  TH.classList.add(styles.RowHeaderWithControls);
};
